// Окно «Создать ведомость»: отметить объекты, на каждый будет своя ведомость. Сценарии 23 и 38.
//
// ## Зачем оно устроено галочками
//
// Заказчик платит по объектам в разные дни: сегодня один, завтра другой, послезавтра сразу
// семь-восемь. 23.09.2026 он спросил, нельзя ли заводить ведомость по отдельности, а не «сразу
// все кучей»; 29.09 повторил голосом: «ведомости по объекту они сразу там в кучу, 70 с лишним
// ведомостей … чтобы поштучно можно было создавать ведомость по каждому объекту». Всё это окно
// умело и раньше, но одним объектом и через выпадающий список, а «все разом» стояло большой
// синей кнопкой без подтверждения — она и завела 77 ведомостей 16.09.
// Оценка прежнего пути: пять-семь нажатий и полминуты на один объект, около пятидесяти нажатий
// на восемь.
//
// ## По умолчанию не отмечено ничего
//
// Типичный заход — один объект или пять-восемь из семидесяти восьми. Если бы «без ведомости»
// были отмечены заранее, их шестьдесят одну пришлось бы СНИМАТЬ, а ошибка при этом — ровно те
// самые «77 в кучу». Галочек человек ставит столько, сколько платит сегодня.
//
// Исключение одно, и оно не про «все»: окно открыли с объектом в адресе (`2026-07~объект`). Так
// зовут его из расчёта человека, где уже видно, что на этом объекте ведомости нет: человек нажал
// «создать», и переспрашивать у него объект значило бы отправить его искать в списке то, что он
// только что назвал. Отмечается тогда ОДИН этот объект, и только если он среди «без ведомости»:
// у объекта с готовой ведомостью выбор пуст, а сам он стоит в «Ведомость уже есть», как раньше.
// Предвыбор ставится ОДИН раз, когда список объектов и реестр месяца приехали оба, а дальше выбор
// принадлежит человеку: снял отметку или отметил другой объект — обновление списка её не вернёт.
// Отмеченную строку один раз подводят к видимой части списка: объектов бывает семьдесят восемь, и
// без этого человек нажимал бы «Создать ведомость», не видя, на какой объект она заведётся.
//
// ## Пустой выбор на сервер не уходит никогда
//
// Для сервера пустой список объектов означает «все действующие» (этим пользуются программа 1.0.16
// и другие клиенты, поэтому его смысл не менялся). Значит клиент обязан сам не прислать пустое:
// нажатие при нуле отмеченных отвечает «Отметьте хотя бы один объект» и не уходит в сеть.
// Единственное исключение — человеку, которому справочник объектов не показывается: отсюда не
// видно, сколько их, и «все» у него идёт через то же подтверждение, но без явного списка.
//
// ## «Все объекты» — ссылкой внизу и с цифрой
//
// Массовое создание необратимо (удалить ведомость нельзя), поэтому оно вторичное действие и идёт
// через окно подтверждения, которое называет число: «Создать 61 ведомость?». Уходят ЯВНЫЕ
// идентификаторы объектов без ведомости, а не пустой список: список в окне и список в запросе
// совпадают буквально.
//
// ## Ключ операции привязан к тому, что создаётся
//
// Сервер сверяет ключ вместе с содержимым запроса (`services/idempotency.py`): тот же ключ с
// другим набором объектов он отклоняет. Поэтому ключ живёт на ОТПЕЧАТОК выбора (месяц и
// отсортированные объекты): другой выбор — другой ключ, а повтор того же выбора после обрыва идёт
// с прежним, и сервер возвращает прежний отчёт вместо второй пачки.
//
// ## Что происходит после создания
//
// Заведённые ведомости сразу пересчитываются по тем же объектам: деньги рождает не создание, а
// пересчёт, и человек, заведший ведомость под сегодняшнюю выплату, без этого снова увидел бы
// нули (инцидент 16.09 был ровно про это). Одна ведомость после этого открывается сама. Несколько —
// список со ссылками и денежным отчётом. «Все объекты» только создают: пересчёт шестидесяти
// ведомостей подряд — долгая операция, и для неё есть отдельная кнопка «Подтянуть часы».
//
// ## Пропуск объекта объясняется поимённо
//
// «Создано 47, пропущено 13» без списка — это тринадцать объектов, про которые человек ничего
// не узнает до конца месяца. Сервер шлёт причину по каждому, и окно её показывает.
//
// ## Административная ведомость стоит здесь же
//
// У неё нет объекта (сценарий 38), поэтому в массовое создание она не попадает никогда: заводят
// её отдельно и на юрлицо. Отдельная панель ради одной кнопки была бы вторым местом, куда надо
// догадаться зайти.

import { useEffect, useRef, useState, type RefObject } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';

import {
  RiArrowDownSLine,
  RiBuilding2Line,
  RiCheckboxCircleLine,
  RiRefreshLine,
} from '@remixicon/react';

import type { GenerateIn, GenerateOut, RecalcPeriodOut } from '@/shared/api/endpoints/payroll';
import type { ApiError } from '@/shared/errors/api-error';
import { newIdempotencyKey } from '@/shared/api/idempotency';
import { useAuthStore } from '@/shared/auth/store';
import { formatRubles } from '@/shared/money/format';
import { useLegalEntitiesQuery, useSitesQuery } from '@/shared/org/queries';
import { can } from '@/shared/rbac/permissions';
import { buildSearch, parseSearch, pushPanel } from '@/shared/routing/search';
import { useСреда } from '@/shared/среда';
import { countLabel, pluralRu } from '@/shared/text/plural';
import {
  formatPeriodKey,
  formatPeriodTitle,
  месяцИзАдресаИлиРасчётный,
  type Period,
} from '@/shared/time/period';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import * as LinkButton from '@/shared/ui/link-button';
import * as Modal from '@/shared/ui/modal';
import { RowCheckbox } from '@/shared/ui/row-checkbox';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { Skeleton } from '@/shared/ui/screen-state';
import { TextField } from '@/shared/ui/text-field';
import { cn } from '@/utils/cn';

import { FILTER_PERIOD, FILTER_SHEET, PANEL_SHEET_RECALC } from '../../route';
import { useCreatePayroll, useGeneratePayrolls, useRecalcPeriod, useRegistry } from '../queries';
import {
  ключПоиска,
  названиеПодходит,
  отпечатокСоздания,
  разделитьОбъекты,
  type ОбъектСВедомостью,
} from '../объекты-периода';

import { Отчёт as ОтчётПересчёта } from './RecalcPeriodPanel';

const EDIT_PERMISSION = 'core.payroll.edit';
const DICTIONARY_PERMISSION = 'core.dictionary.view';
/** Поле поиска опознаётся по адресу: на мыши оно берёт курсор само (см. ниже). */
const ПОЛЕ_ПОИСКА = 'sheet-generate-search';
/** Сколько заведённых ведомостей перечислять: остальные видны в списке слева. */
const ПОКАЗАТЬ_ЗАВЕДЁННЫХ = 12;
/**
 * Через сколько подводить отмеченную строку к видимой части списка. Панель выезжает 200 мс, и пока
 * она едет, строка стоит за правым краем окна, а `scrollIntoView` доводит строку во ВСЕХ
 * прокручиваемых предках, в том числе скрытых (`overflow: hidden`): оболочка снаружи дёрнулась бы
 * вбок. Ждём, пока панель остановится.
 */
const ПОДВЕСТИ_ЧЕРЕЗ_МС = 250;

const ВЕДОМОСТИ = ['ведомость', 'ведомости', 'ведомостей'] as const;
const ОБЪЕКТЫ_У = ['объекта', 'объектов', 'объектов'] as const;

const НЕТ_ПРАВА =
  'Ведомости заводит финансовый отдел или администратор. Скопируйте ссылку на этот месяц и передайте им: окно создания откроется у них на том же месяце.';
const НИЧЕГО_НЕ_ОТМЕЧЕНО = 'Отметьте хотя бы один объект';

/** Чем кончилось создание: отчёт сервера и то, что вышло с пересчётом часов. */
interface Итог {
  отчёт: GenerateOut;
  /** Пересчёт по тем же объектам. `null` — он не запускался или не вышел. */
  пересчёт: RecalcPeriodOut | null;
  /** Пересчёт запускался и оборвался: деньги не подтянуты, и об этом надо сказать. */
  пересчётНеВышел: boolean;
}

/**
 * Правда ли, что операция идёт уже дольше `мс`.
 *
 * Пояснение «это может занять несколько секунд» нужно тому, кто ждёт долго. Показанное сразу оно
 * только двигало бы панель выделения на строку при каждом нажатии: быстрая операция (а их
 * большинство) заканчивалась бы раньше, чем его успели бы прочитать.
 */
function useДолго(идёт: boolean, мс = 1500): boolean {
  const [долго, setДолго] = useState(false);
  useEffect(() => {
    if (!идёт) {
      setДолго(false);
      return;
    }
    const таймер = window.setTimeout(() => setДолго(true), мс);
    return () => window.clearTimeout(таймер);
  }, [идёт, мс]);
  return долго;
}

/**
 * Опознаватель панели: месяц и, когда окно открыли с объекта, сам объект.
 *
 * «2026-07» либо «2026-07~объект». Разделитель волной, а не двоеточием: двоеточие в адресе
 * отделяет ВИД панели от опознавателя (`?p=вид:id`), и второе внутри него разобралось бы не
 * туда. Так же устроена панель тарифов (`TariffPanel`). Пустой хвост значит «объекта нет»;
 * годен ли непустой, решает список объектов, а не разбор.
 */
function разобратьОпознаватель(id: string): {
  ключМесяца: string;
  предвыбранныйОбъект: string | null;
} {
  const волна = id.indexOf('~');
  if (волна === -1) {
    return { ключМесяца: id, предвыбранныйОбъект: null };
  }
  const хвост = id.slice(волна + 1);
  return { ключМесяца: id.slice(0, волна), предвыбранныйОбъект: хвост === '' ? null : хвост };
}

export function GeneratePanel({ id }: { id: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const среда = useСреда();
  const права = useAuthStore((state) => state.permissions);
  const можноПравить = can(права, EDIT_PERMISSION);
  const виднысправочники = can(права, DICTIONARY_PERMISSION);

  // Месяц приезжает ключом в адресе панели: ссылка на окно открывается тем же месяцем.
  const { ключМесяца, предвыбранныйОбъект } = разобратьОпознаватель(id);
  const период = месяцИзАдресаИлиРасчётный(ключМесяца);
  const месяц = formatPeriodTitle(период).toLowerCase();

  const реестр = useRegistry(период);
  const объекты = useSitesQuery({ active: true }, виднысправочники);
  const создание = useGeneratePayrolls();
  const пересчёт = useRecalcPeriod();
  const административная = useCreatePayroll();

  const [выбор, setВыбор] = useState<ReadonlySet<string>>(() => new Set());
  // Предвыбор из адреса уже применён или нет: он делается один раз, см. ниже.
  const [предвыборПрименён, setПредвыборПрименён] = useState(false);
  // Отмеченному объекту надо стоять на виду: объектов бывает семьдесят восемь, а он мог оказаться
  // ниже видимой части списка. Признак ставится один раз вместе с предвыбором, а строку держит
  // ссылка, которая есть только у предвыбранного объекта.
  const [подвести, setПодвести] = useState(false);
  const строкаПредвыбора = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (!подвести) {
      return;
    }
    const таймер = window.setTimeout(() => {
      // `nearest`: строка и так видна — не двигаем ничего, ниже или выше — ровно настолько, чтобы
      // показать её. Прокручивается её собственный список, а не страница.
      строкаПредвыбора.current?.scrollIntoView({ block: 'nearest' });
    }, ПОДВЕСТИ_ЧЕРЕЗ_МС);
    return () => window.clearTimeout(таймер);
  }, [подвести]);
  const [поиск, setПоиск] = useState('');
  const [запрет, setЗапрет] = useState<string | null>(null);
  const [окноВсех, setОкноВсех] = useState(false);
  const [итог, setИтог] = useState<Итог | null>(null);
  // Ключи живут столько же, сколько окно: закрыли и открыли заново — ключи новые.
  const ключи = useRef(new Map<string, string>());

  // На мыши поле поиска берёт курсор сразу: «нажал «Создать ведомость», набрал название, отметил».
  // Под палец экранная клавиатура, выскочившая при открытии, закрыла бы половину списка.
  const курсорВзят = useRef(false);
  useEffect(() => {
    // Один раз при открытии: смена среды посреди работы курсор уже не двигает.
    if (курсорВзят.current || среда.ввод !== 'мышь' || !виднысправочники) {
      return;
    }
    курсорВзят.current = true;
    document.getElementById(ПОЛЕ_ПОИСКА)?.focus({ preventScroll: true });
  }, [среда.ввод, виднысправочники]);

  const заведено = реестр.data?.items ?? [];
  const объектовые = заведено.filter((пункт) => !пункт.is_administrative);
  const состав =
    объекты.data !== undefined && реестр.data !== undefined
      ? разделитьОбъекты(объекты.data.items, реестр.data.items)
      : null;
  // Объект из адреса отмечается ОДИН раз: в тот миг, когда список объектов и реестр месяца приехали
  // оба и стало видно, есть ли у него ведомость. Раньше этого не знает никто (список приезжает
  // позже окна), а позже отметки принадлежат человеку, и обновление списка их не трогает.
  //
  // Прямо при отрисовке, а не эффектом: эффект оставил бы на кадр список без отметки, и человек
  // видел бы, как галочка появляется сама. React допускает обновление собственного состояния при
  // отрисовке и перерисовывает компонент до того, как что-то попадёт на экран; условие гасит
  // повтор (так же устроено смахивание в `PanelStack`).
  if (!предвыборПрименён && состав !== null) {
    setПредвыборПрименён(true);
    if (
      предвыбранныйОбъект !== null &&
      состав.безВедомости.some((объект) => объект.id === предвыбранныйОбъект)
    ) {
      setВыбор(new Set([предвыбранныйОбъект]));
      setПодвести(true);
    }
  }
  // Отмечено считается ПО ТЕМ, кого ещё можно создать: пока окно открыто, кто-то другой мог завести
  // ведомость на отмеченный объект, и он должен перестать быть в счёте, а не уйти в запрос.
  const отмеченные = состав === null ? [] : состав.безВедомости.filter((о) => выбор.has(о.id));
  // Ответ мог потеряться по дороге, когда ведомости уже заведены: после ошибки реестр перечитан, и
  // эти объекты ушли из галочек в группу «Ведомость уже есть». Человеку надо сказать, куда они
  // делись, иначе он видит пустой выбор там, где только что отмечал.
  const заведеныПоВыбору =
    состав === null ? 0 : состав.сВедомостью.filter(({ объект }) => выбор.has(объект.id)).length;
  const занято = создание.isPending || пересчёт.isPending;
  const идётДолго = useДолго(занято);

  function адресВедомости(payrollId: string): string {
    const состояние = parseSearch(location.search);
    return `${location.pathname}${buildSearch({
      ...состояние,
      filters: {
        ...состояние.filters,
        [FILTER_PERIOD]: formatPeriodKey(период),
        [FILTER_SHEET]: payrollId,
      },
      panels: [],
    })}`;
  }

  function открытьВедомость(payrollId: string) {
    void navigate(адресВедомости(payrollId));
  }

  function открытьПересчётПериода() {
    const состояние = parseSearch(location.search);
    void navigate({
      pathname: location.pathname,
      search: buildSearch(
        pushPanel(состояние, { view: PANEL_SHEET_RECALC, id: formatPeriodKey(период) }),
      ),
    });
  }

  function отметить(объектId: string) {
    if (занято) {
      return;
    }
    setЗапрет(null);
    // Ошибка относилась к прежнему выбору: с новым она уже про другое.
    создание.reset();
    setВыбор((прежний) => {
      const следующий = new Set(прежний);
      if (следующий.has(объектId)) {
        следующий.delete(объектId);
      } else {
        следующий.add(объектId);
      }
      return следующий;
    });
  }

  function ключДля(отпечаток: string): string {
    let ключ = ключи.current.get(отпечаток);
    if (ключ === undefined) {
      ключ = newIdempotencyKey();
      ключи.current.set(отпечаток, ключ);
    }
    return ключ;
  }

  /**
   * Создать ведомости. `объектыИд === null` — «все, что видит сервер»: только для человека без
   * справочника. Пустой список сюда не доходит никогда: его судьба — «Отметьте хотя бы один».
   */
  function создать(объектыИд: readonly string[] | null, пересчитывать: boolean) {
    if (занято) {
      return;
    }
    if (!можноПравить) {
      // Кнопка не сереет без права: выключенная не доводит нажатие до обработчика, и человек жмёт
      // в пустоту. Причина звучит по нажатию (правило 10).
      setЗапрет(НЕТ_ПРАВА);
      setОкноВсех(false);
      return;
    }
    if (объектыИд !== null && объектыИд.length === 0) {
      setЗапрет(НИЧЕГО_НЕ_ОТМЕЧЕНО);
      // Окно подтверждения «все объекты» при этом закрывается: пока оно было открыто, ведомости на
      // остальные объекты мог завести кто-то другой, и подтверждать стало нечего. Оставленное
      // открытым, оно отвечало бы на нажатие молчанием.
      setОкноВсех(false);
      return;
    }
    setЗапрет(null);
    const тело: GenerateIn =
      объектыИд === null
        ? { year: период.year, month: период.month }
        : { year: период.year, month: период.month, site_ids: [...объектыИд].sort() };
    создание.mutate(
      { body: тело, idempotencyKey: ключДля(отпечатокСоздания(период, объектыИд)) },
      {
        onSuccess: (отчёт) => {
          const естьВедомости = (отчёт.sites ?? []).some((пункт) => пункт.payroll_id !== null);
          if (!пересчитывать || объектыИд === null || !естьВедомости) {
            завершить({ отчёт, пересчёт: null, пересчётНеВышел: false }, объектыИд);
            return;
          }
          пересчёт.mutate(
            { year: период.year, month: период.month, site_ids: [...объектыИд].sort() },
            {
              onSuccess: (посчитано) =>
                завершить({ отчёт, пересчёт: посчитано, пересчётНеВышел: false }, объектыИд),
              onError: () => завершить({ отчёт, пересчёт: null, пересчётНеВышел: true }, объектыИд),
            },
          );
        },
        onSettled: () => setОкноВсех(false),
      },
    );
  }

  function завершить(результат: Итог, объектыИд: readonly string[] | null) {
    // Одна ведомость открывается сама, и человеку не приходится искать её в списке слева. Если
    // часы подтянуть не вышло, окно остаётся: здесь сказано, что делать, а в пустой ведомости
    // этого не видно.
    const одна =
      объектыИд?.length === 1 && результат.пересчёт !== null
        ? (результат.отчёт.sites ?? []).find((пункт) => пункт.payroll_id !== null)
        : undefined;
    if (одна !== undefined && одна.payroll_id !== null) {
      открытьВедомость(одна.payroll_id);
      return;
    }
    setИтог(результат);
  }

  const подписьКнопки = создание.isPending
    ? 'Заводим…'
    : пересчёт.isPending
      ? 'Подтягиваем часы…'
      : отмеченные.length >= 2
        ? `Создать ${countLabel(отмеченные.length, ВЕДОМОСТИ)}`
        : 'Создать ведомость';

  // ── Окно без справочника объектов ───────────────────────────────────────────────────────────
  if (!виднысправочники) {
    return (
      <div className='flex flex-col gap-4'>
        <p className='text-paragraph-sm text-text-sub-600'>
          За {месяц}. Список объектов вам не показывается, поэтому отметить отдельные объекты
          нельзя.
        </p>
        {итог !== null ? (
          <ИтогСоздания
            итог={итог}
            период={период}
            адресВедомости={адресВедомости}
            onRecalcPanel={открытьПересчётПериода}
          />
        ) : (
          <div className='flex flex-col items-start gap-2'>
            <p className='text-paragraph-sm text-text-strong-950'>
              Сколько объектов без ведомости, отсюда не видно
            </p>
            <LinkButton.Root
              type='button'
              variant='primary'
              size='medium'
              underline
              onClick={() => {
                if (!можноПравить) {
                  setЗапрет(НЕТ_ПРАВА);
                  return;
                }
                setЗапрет(null);
                setОкноВсех(true);
              }}
            >
              Создать на все объекты без ведомости
            </LinkButton.Root>
            {запрет !== null && <Hint.Root hasError>{запрет}</Hint.Root>}
            {создание.error !== null && (
              <ОшибкаСоздания ошибка={создание.error} месяц={месяц} заведено={объектовые.length} />
            )}
          </div>
        )}
        <ПодтверждениеВсех
          открыто={окноВсех}
          месяц={месяц}
          сколько={null}
          ужеЕсть={null}
          занято={создание.isPending}
          onOpenChange={setОкноВсех}
          onConfirm={() => создать(null, false)}
        />
        <АдминистративнаяВедомость
          период={период}
          можноПравить={можноПравить}
          заведена={заведено.some((пункт) => пункт.is_administrative)}
          создание={административная}
        />
      </div>
    );
  }

  // ── Итог создания: окно показывает, что вышло ───────────────────────────────────────────────
  if (итог !== null) {
    return (
      <div className='flex flex-col gap-4'>
        <ИтогСоздания
          итог={итог}
          период={период}
          адресВедомости={адресВедомости}
          onRecalcPanel={открытьПересчётПериода}
        />
        <АдминистративнаяВедомость
          период={период}
          можноПравить={можноПравить}
          заведена={заведено.some((пункт) => пункт.is_administrative)}
          создание={административная}
        />
      </div>
    );
  }

  const запрос = ключПоиска(поиск);
  const безВедомости = состав?.безВедомости ?? [];
  const подходящие = безВедомости.filter((объект) => названиеПодходит(объект.name, запрос));
  const ужеЕстьВсего = состав?.сВедомостью ?? [];
  const ужеЕсть = ужеЕстьВсего.filter(({ объект }) => названиеПодходит(объект.name, запрос));

  return (
    // `min-h-full`: корень занимает высоту окна, и панель выделения прижимается к низу даже тогда,
    // когда список короткий. Поле панели (16 точек) лежит на прокручиваемой обёртке каркаса, и
    // панель выделения снимает его отрицательным отступом, чтобы встать вплотную к краям.
    <div className='flex min-h-full flex-col gap-4'>
      <p className='text-paragraph-sm text-text-sub-600'>
        За {месяц}. Отметьте объекты: на каждый будет своя ведомость.
      </p>

      <TextField
        id={ПОЛЕ_ПОИСКА}
        label='Найти объект'
        value={поиск}
        onChange={setПоиск}
        placeholder='Начните вводить название'
      />

      {состав !== null && (
        <p className='text-paragraph-sm text-text-strong-950' aria-live='polite'>
          {`Без ведомости: ${безВедомости.length} из ${объекты.data?.items.length ?? 0}. Отмечено: ${отмеченные.length}`}
        </p>
      )}

      {реестр.error !== null && реестр.data === undefined ? (
        // Ноль здесь — «не знаем», а не «ведомостей нет». Показать список объектов без ведомостей
        // поверх упавшего реестра значило бы соврать ровно там, где человек решает нажимать.
        <div className='flex flex-col items-start gap-2 rounded-lg bg-bg-weak-50 px-4 py-3'>
          <span className='text-paragraph-sm text-text-strong-950'>
            Сколько ведомостей уже заведено, сейчас не видно: список за {месяц} не приехал.
            Создавать вслепую не стоит: повторите запрос.
          </span>
          <Button.Root
            variant='neutral'
            mode='stroke'
            size='xxsmall'
            onClick={() => void реестр.refetch()}
          >
            Повторить
          </Button.Root>
        </div>
      ) : объекты.error !== null && объекты.data === undefined ? (
        <div className='flex flex-col items-start gap-2 rounded-lg bg-bg-weak-50 px-4 py-3'>
          <span className='text-paragraph-sm text-text-strong-950'>
            Список объектов не приехал, поэтому отметить нечего. Повторите запрос.
          </span>
          <Button.Root
            variant='neutral'
            mode='stroke'
            size='xxsmall'
            onClick={() => void объекты.refetch()}
          >
            Повторить
          </Button.Root>
        </div>
      ) : состав === null ? (
        // Скелет — ровно на месте будущих строк и той же высоты: на заглушку, не похожую на
        // содержимое, человек смотрит как на поломку.
        <div aria-hidden='true' className='flex flex-col gap-1'>
          {Array.from({ length: 6 }, (_нет, номер) => (
            <Skeleton key={номер} className='h-11 w-full rounded-lg' />
          ))}
        </div>
      ) : (
        <>
          {подходящие.length > 0 ? (
            <ul aria-label='Объекты без ведомости' className='flex flex-col' aria-busy={занято}>
              {подходящие.map((объект) => (
                <СтрокаОбъекта
                  key={объект.id}
                  id={`sheet-pick-${объект.id}`}
                  название={объект.name}
                  город={объект.city}
                  отмечен={выбор.has(объект.id)}
                  onToggle={() => отметить(объект.id)}
                  строкаRef={объект.id === предвыбранныйОбъект ? строкаПредвыбора : undefined}
                />
              ))}
            </ul>
          ) : (
            <p className='rounded-lg bg-bg-weak-50 px-4 py-3 text-paragraph-sm text-text-sub-600'>
              {безВедомости.length === 0
                ? объекты.data !== undefined && объекты.data.items.length === 0
                  ? 'Действующих объектов нет. Объекты заводятся в разделе «Объекты».'
                  : `На все объекты за ${месяц} ведомости уже заведены.`
                : 'Объектов без ведомости с таким названием нет.'}
            </p>
          )}

          <ГруппаУжеЕсть
            всего={ужеЕстьВсего.length}
            строки={ужеЕсть}
            ищем={запрос !== ''}
            адресВедомости={адресВедомости}
          />

          {безВедомости.length >= 2 && (
            <div>
              <LinkButton.Root
                type='button'
                variant='primary'
                size='medium'
                underline
                onClick={() => {
                  if (!можноПравить) {
                    setЗапрет(НЕТ_ПРАВА);
                    return;
                  }
                  setЗапрет(null);
                  setОкноВсех(true);
                }}
              >
                Создать на все объекты без ведомости ({безВедомости.length})
              </LinkButton.Root>
            </div>
          )}
        </>
      )}

      <АдминистративнаяВедомость
        период={период}
        можноПравить={можноПравить}
        заведена={заведено.some((пункт) => пункт.is_administrative)}
        создание={административная}
      />

      {/* Панель выделения: у неё есть смысл (что отмечено и что с этим делать), а не только
          кнопка на подложке. Она липнет к низу окна, пока список прокручивают, и на планшете
          не уходит под полосу жеста «домой». Когда отмечать некого, панели нет вовсе: на «все
          объекты уже с ведомостями» её кнопка отвечала бы «отметьте хотя бы один» там, где
          отмечать нечего. */}
      {безВедомости.length > 0 && (
        <div
          role='group'
          aria-label='Создание ведомостей'
          className='sticky -bottom-4 z-10 -mx-4 -mb-4 mt-auto flex flex-col gap-2 border-t border-stroke-soft-200 bg-bg-white-0 px-4 py-3 тач:безопасно-снизу'
        >
          {создание.error !== null && (
            <ОшибкаСоздания
              ошибка={создание.error}
              месяц={месяц}
              заведено={объектовые.length}
              изОтмеченныхЗаведено={заведеныПоВыбору}
            />
          )}
          {запрет !== null && (
            <Hint.Root hasError role='alert'>
              {запрет}
            </Hint.Root>
          )}
          {идётДолго && (
            <p className='text-paragraph-xs text-text-sub-600' aria-live='polite'>
              Заводим ведомости и подтягиваем часы из табеля: это может занять несколько секунд.
            </p>
          )}
          <div className='flex items-center justify-between gap-3'>
            {отмеченные.length > 0 ? (
              <LinkButton.Root
                type='button'
                variant='gray'
                size='medium'
                underline
                onClick={() => {
                  setЗапрет(null);
                  создание.reset();
                  setВыбор(new Set());
                }}
              >
                Снять отметки
              </LinkButton.Root>
            ) : (
              <span />
            )}
            <Button.Root
              type='button'
              variant='primary'
              mode='filled'
              size='small'
              onClick={() =>
                создать(
                  отмеченные.map((объект) => объект.id),
                  true,
                )
              }
            >
              {подписьКнопки}
            </Button.Root>
          </div>
        </div>
      )}

      <ПодтверждениеВсех
        открыто={окноВсех}
        месяц={месяц}
        сколько={безВедомости.length}
        ужеЕсть={ужеЕстьВсего.length}
        занято={создание.isPending}
        onOpenChange={setОкноВсех}
        onConfirm={() =>
          создать(
            безВедомости.map((объект) => объект.id),
            false,
          )
        }
      />
    </div>
  );
}

/**
 * Строка объекта без ведомости. Нажимается вся строка: название и город тоже часть галочки.
 *
 * `scroll-mb-28`: панель выделения липнет к низу окна (около 60 точек, на планшете выше), и строка,
 * подведённая `scrollIntoView` к самому краю, оказалась бы под ней. Поле прокрутки оставляет её
 * над панелью.
 */
function СтрокаОбъекта({
  id,
  название,
  город,
  отмечен,
  onToggle,
  строкаRef,
}: {
  id: string;
  название: string;
  город: string | null;
  отмечен: boolean;
  onToggle: () => void;
  /** Ссылка на строку: есть у объекта из адреса панели, которого подводят к видимой части. */
  строкаRef?: RefObject<HTMLLIElement>;
}) {
  return (
    <li ref={строкаRef} className='scroll-mb-28 border-b border-stroke-soft-200 last:border-b-0'>
      {/* Высота 44: строку нажимают пальцем, и промах в шестнадцатиточечную галочку здесь стоит
          лишней ведомости. Метка охватывает всю строку, а не только квадрат. */}
      <label
        htmlFor={id}
        className='flex min-h-11 cursor-pointer items-center gap-2 rounded-lg px-1 transition duration-200 ease-out hover:bg-bg-weak-50'
      >
        <RowCheckbox
          id={id}
          label={`Выбрать: ${название}`}
          checked={отмечен}
          onCheckedChange={onToggle}
        />
        {/* Название переносится, а не обрезается: у заказчика город стоит В КОНЦЕ названия
            («Радиус-Сервис г. Ноябрьск» и «Радиус-Сервис г. Пыть-Ях»), и обрубок «Радиус-Сервис…»
            у двух строк подряд выглядит одинаково. Ошибиться здесь нельзя: ведомость, заведённую
            не на тот объект, удалить нечем. */}
        <span className='flex min-w-0 flex-1 flex-col'>
          <span className='break-words text-label-sm text-text-strong-950'>{название}</span>
          {город !== null && (
            <span className='break-words text-paragraph-xs text-text-sub-600'>{город}</span>
          )}
        </span>
      </label>
    </li>
  );
}

/**
 * «Ведомость уже есть (17)»: свёрнуто и серым, со ссылкой «Открыть» у каждой.
 *
 * Они не пропадают из окна, чтобы человек, не нашедший объект среди галочек, не решил, что его
 * нет вовсе. При поиске группа раскрывается сама: названное человеком объект должен найтись, а
 * не спрятаться под заголовком.
 */
function ГруппаУжеЕсть({
  всего,
  строки,
  ищем,
  адресВедомости,
}: {
  всего: number;
  строки: readonly ОбъектСВедомостью[];
  ищем: boolean;
  адресВедомости: (payrollId: string) => string;
}) {
  const [раскрыто, setРаскрыто] = useState(false);
  if (всего === 0 || (ищем && строки.length === 0)) {
    return null;
  }
  const виден = раскрыто || ищем;
  return (
    <div className='flex flex-col'>
      <button
        type='button'
        aria-expanded={виден}
        onClick={() => setРаскрыто((было) => !было)}
        className='цель-тача relative flex items-center gap-1 self-start rounded-md py-1 text-label-sm text-text-sub-600 transition duration-200 ease-out hover:text-text-strong-950'
      >
        Ведомость уже есть ({ищем ? строки.length : всего})
        <RiArrowDownSLine
          className={cn('size-4 transition-transform duration-200 ease-out', виден && 'rotate-180')}
        />
      </button>
      {виден && (
        <ul aria-label='Объекты, где ведомость уже есть' className='flex flex-col'>
          {строки.map(({ объект, ведомость }) => (
            <СтрокаВедомости
              key={объект.id}
              название={объект.name}
              подпись={объект.city ?? undefined}
              адрес={адресВедомости(ведомость.id)}
              приглушена
            />
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Строка с названием, подписью и ссылкой «Открыть».
 *
 * Одна на три места: объекты, где ведомость уже есть, заведённые только что и пропущенные с
 * причиной. Серая — когда человеку там нечего делать, кроме как открыть.
 */
function СтрокаВедомости({
  название,
  подпись,
  адрес,
  приглушена = false,
}: {
  название: string;
  подпись?: string;
  адрес?: string;
  приглушена?: boolean;
}) {
  return (
    <li className='flex min-h-11 items-center gap-3 border-b border-stroke-soft-200 px-1 py-1.5 last:border-b-0'>
      <span className='flex min-w-0 flex-1 flex-col'>
        <span
          className={cn('text-label-sm', приглушена ? 'text-text-sub-600' : 'text-text-strong-950')}
        >
          {название}
        </span>
        {подпись !== undefined && (
          <span className='text-paragraph-xs text-text-sub-600'>{подпись}</span>
        )}
      </span>
      {адрес !== undefined && (
        <LinkButton.Root variant='primary' size='medium' underline asChild>
          <Link to={адрес} aria-label={`Открыть ведомость: ${название}`}>
            Открыть
          </Link>
        </LinkButton.Root>
      )}
    </li>
  );
}

/**
 * Ошибка создания и состояние мира после неё.
 *
 * После красной плашки человек спрашивает ровно одно: создалось или нет. Отвечаем состоянием
 * мира, а не пересказом запроса: реестр перечитан (`onSettled` у мутации), и число здесь свежее.
 */
function ОшибкаСоздания({
  ошибка,
  месяц,
  заведено,
  изОтмеченныхЗаведено = 0,
}: {
  ошибка: unknown;
  месяц: string;
  заведено: number;
  /** Сколько из отмеченных объектов ведомость уже получили, пока ответ шёл. */
  изОтмеченныхЗаведено?: number;
}) {
  return (
    <div className='flex flex-col gap-2'>
      <ErrorAlert error={ошибка} />
      <div className='flex flex-col gap-1 rounded-lg bg-bg-weak-50 px-4 py-3'>
        <span className='text-paragraph-sm text-text-strong-950'>
          Сейчас за {месяц} заведено {countLabel(заведено, ВЕДОМОСТИ)} по объектам.
        </span>
        {изОтмеченныхЗаведено > 0 && (
          <span className='text-paragraph-sm text-text-strong-950'>
            Из отмеченных ведомость уже заведена у {изОтмеченныхЗаведено}{' '}
            {pluralRu(изОтмеченныхЗаведено, ОБЪЕКТЫ_У)}: они перешли в список «Ведомость уже есть».
          </span>
        )}
        <span className='text-paragraph-xs text-text-sub-600'>
          Повторить безопасно: окно помнит эту попытку, и второй ведомости на объект не будет.
        </span>
      </div>
    </div>
  );
}

/**
 * Подтверждение «на все объекты без ведомости».
 *
 * Модалка здесь ровно по назначению (правило 4): действие необратимо, удалить заведённые
 * ведомости нельзя. Называет число, а не спрашивает «вы уверены»: уверенность человека не
 * информация. `сколько === null` — справочник объектов человеку не показывается и посчитать
 * нечем; тогда окно говорит об этом прямо, а не выдумывает цифру.
 */
function ПодтверждениеВсех({
  открыто,
  месяц,
  сколько,
  ужеЕсть,
  занято,
  onOpenChange,
  onConfirm,
}: {
  открыто: boolean;
  месяц: string;
  сколько: number | null;
  ужеЕсть: number | null;
  занято: boolean;
  onOpenChange: (открыто: boolean) => void;
  onConfirm: () => void;
}) {
  const заголовок =
    сколько === null
      ? 'Создать ведомости на все объекты?'
      : `Создать ${countLabel(сколько, ВЕДОМОСТИ)}?`;
  const описание =
    сколько === null
      ? `За ${месяц} будет заведено по ведомости на каждый действующий объект, где её ещё нет. Сколько их, отсюда не видно. Где ведомость уже есть, ничего не тронем. Удалить заведённые ведомости нельзя.`
      : `За ${месяц} будет заведено по ведомости на каждый действующий объект, где её ещё нет: ${сколько}. ${
          ужеЕсть !== null && ужеЕсть > 0
            ? `У ${ужеЕсть} ${pluralRu(ужеЕсть, ОБЪЕКТЫ_У)} ведомость уже есть, их не тронем. `
            : ''
        }Удалить заведённые ведомости нельзя.`;
  return (
    <Modal.Root
      open={открыто}
      onOpenChange={(следующее) => {
        // Пока запрос идёт, окно не закрывается: закрытое окно выглядело бы как «отменил», а
        // ведомости при этом заводятся.
        if (!занято) {
          onOpenChange(следующее);
        }
      }}
    >
      <Modal.Content>
        <Modal.Header title={заголовок} description={описание} />
        <Modal.Footer>
          <Modal.Close asChild>
            <Button.Root
              type='button'
              variant='neutral'
              mode='stroke'
              size='small'
              className='w-full'
            >
              Не создавать
            </Button.Root>
          </Modal.Close>
          <Button.Root
            type='button'
            variant='primary'
            mode='filled'
            size='small'
            className='w-full'
            onClick={onConfirm}
          >
            {занято ? 'Заводим…' : сколько === null ? 'Создать' : `Создать ${сколько}`}
          </Button.Root>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
}

/**
 * Итог создания нескольких ведомостей. Кончается не статусом, а СЛЕДУЮЩИМ ШАГОМ.
 *
 * ## Почему здесь есть «Подтянуть часы»
 *
 * 16.09.2026 заказчик создал 77 ведомостей за июль и увидел реестр нулей. Нули были честные:
 * создание ведомости денег не считает, часы приходят из табеля и снимаются ОТДЕЛЬНЫМ
 * пересчётом. Но узнать это было неоткуда — окно писало «создано 77» и замолкало, — и человек
 * ушёл спрашивать в переписку, перенесли ли мы вообще его данные.
 *
 * Поэтому, когда деньги не подтянуты (создавали «все объекты» либо пересчёт оборвался), итог
 * называет число, которое человека и волнует: сколько ведомостей ещё без денег, — и даёт одно
 * нажатие туда, где это чинится сразу для всего месяца.
 */
function ИтогСоздания({
  итог,
  период,
  адресВедомости,
  onRecalcPanel,
}: {
  итог: Итог;
  период: Period;
  адресВедомости: (payrollId: string) => string;
  onRecalcPanel: () => void;
}) {
  const { отчёт, пересчёт, пересчётНеВышел } = итог;
  const пункты = отчёт.sites ?? [];
  // Повтор после обрыва: сервер отвечает «заведено этой же операцией», и для человека это те же
  // заведённые ведомости, а не пропущенные.
  const заведена = (пункт: (typeof пункты)[number]) =>
    пункт.payroll_id !== null && (пункт.created || отчёт.repeated);
  const заведённые = пункты.filter(заведена);
  const пропущенные = пункты.filter((пункт) => !заведена(пункт));
  // «Без расчёта» — заведённые сейчас ведомости со строками: создание денег не считает, и пока
  // пересчёт не прошёл, расчёта по ним нет по самому устройству. Берётся из отчёта, а не из
  // реестра: реестр после создания перечитывается, и пока он едет, число в нём устарело бы.
  // Считаем только ведомости ЭТОГО создания: «в них» говорит про них, а не про весь месяц.
  const безРасчёта = заведённые.filter((пункт) => пункт.rows > 0).length;
  const деньги = new Map((пересчёт?.sheets ?? []).map((лист) => [лист.payroll_id, лист]));
  const пусто = пункты.length === 0 && отчёт.created === 0;

  return (
    <div className='flex flex-col gap-3'>
      <div className='flex items-start gap-2 rounded-lg bg-bg-weak-50 px-4 py-3'>
        <RiCheckboxCircleLine className='mt-0.5 size-4 shrink-0 text-success-base' />
        <div className='flex flex-col gap-0.5'>
          <span className='text-paragraph-sm text-text-strong-950'>{отчёт.summary}</span>
          <span className='text-paragraph-xs text-text-sub-600'>
            Строк собрано {отчёт.rows}, людей {отчёт.employees}.
            {/* «Окно уже отработало с этим ключом» — наше слово про защиту от повтора, а не его
                (аудит 23.09.2026). Человеку важен итог: второй пачки не появилось. */}
            {отчёт.repeated &&
              ' Это повторное нажатие: ведомости созданы с первого раза, вторых не появилось.'}
          </span>
        </div>
      </div>

      {пусто && (
        <p className='text-paragraph-sm text-text-sub-600'>
          Ни одной ведомости не заведено: отмеченных объектов нет среди действующих. Закройте окно и
          откройте заново.
        </p>
      )}

      {заведённые.length > 0 && (
        // Заголовка «Заведено: N» нет намеренно: число уже названо фразой сервера выше («создано
        // ведомостей: 3, людей 12»), и второй раз тот же факт читался бы как второй. Строки
        // со ссылками идут сразу под ней.
        <div className='flex flex-col gap-1'>
          <ul aria-label='Заведённые ведомости' className='flex flex-col'>
            {заведённые.slice(0, ПОКАЗАТЬ_ЗАВЕДЁННЫХ).map((пункт) => {
              const лист = деньги.get(пункт.payroll_id ?? '');
              return (
                <СтрокаВедомости
                  key={пункт.site_id}
                  название={пункт.site_name}
                  подпись={[
                    countLabel(пункт.employees, ['человек', 'человека', 'человек']),
                    лист === undefined
                      ? null
                      : лист.total_payout > 0
                        ? `к выплате ${formatRubles(лист.total_payout)}`
                        : 'денег пока нет',
                  ]
                    .filter((часть) => часть !== null)
                    .join(' · ')}
                  адрес={пункт.payroll_id === null ? undefined : адресВедомости(пункт.payroll_id)}
                />
              );
            })}
          </ul>
          {заведённые.length > ПОКАЗАТЬ_ЗАВЕДЁННЫХ && (
            <span className='text-paragraph-xs text-text-sub-600'>
              и ещё {заведённые.length - ПОКАЗАТЬ_ЗАВЕДЁННЫХ}: они в списке ведомостей месяца
            </span>
          )}
        </div>
      )}

      {пересчёт !== null && <ОтчётПересчёта итог={пересчёт} />}

      {пересчёт === null && безРасчёта > 0 && (
        <div className='flex flex-col items-start gap-2 rounded-lg bg-bg-weak-50 px-4 py-3'>
          <div className='flex flex-col gap-0.5'>
            <span className='text-paragraph-sm text-text-strong-950'>
              Денег в них пока нет: {countLabel(безРасчёта, ВЕДОМОСТИ)} без расчёта.
            </span>
            <span className='text-paragraph-xs text-text-sub-600'>
              {пересчётНеВышел
                ? 'Часы подтянуть сразу не удалось. '
                : 'Так и задумано: создание собирает людей и строки, а часы приходят из табеля и снимаются отдельным пересчётом. '}
              Нажмите: посчитаем весь месяц разом и скажем, чего не хватило.
            </span>
          </div>
          <Button.Root variant='primary' mode='filled' size='xsmall' onClick={onRecalcPanel}>
            <Button.Icon as={RiRefreshLine} />
            Подтянуть часы за {formatPeriodTitle(период).toLowerCase()}
          </Button.Root>
        </div>
      )}

      {пропущенные.length > 0 && (
        <div className='flex flex-col gap-1'>
          <span className='text-label-sm text-text-strong-950'>
            Пропущено: {countLabel(пропущенные.length, ['объект', 'объекта', 'объектов'])}
          </span>
          <ul aria-label='Пропущенные объекты' className='flex flex-col'>
            {пропущенные.map((пункт) => (
              <СтрокаВедомости
                key={пункт.site_id}
                название={пункт.site_name}
                подпись={
                  пункт.reason ?? 'причина не названа; так быть не должно, сообщите разработчикам'
                }
                адрес={пункт.payroll_id === null ? undefined : адресВедомости(пункт.payroll_id)}
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Ведомость аппарата управления: объекта нет, юрлицо обязательно (сценарий 38). */
function АдминистративнаяВедомость({
  период,
  можноПравить,
  заведена,
  создание,
}: {
  период: Period;
  можноПравить: boolean;
  заведена: boolean;
  создание: ReturnType<typeof useCreatePayroll>;
}) {
  const юрлица = useLegalEntitiesQuery();
  const [юрлицо, setЮрлицо] = useState<string>(NO_VALUE);
  const [запрет, setЗапрет] = useState<string | null>(null);
  // «Справочник пуст», «справочник не приехал» и «прав на справочник нет» — разные вещи. Общая
  // подпись «юрлиц нет» вместе с выключенным полем даёт замкнутый круг: поле не нажимается, а
  // причина названа неверно.
  const проЮрлица =
    юрлица.error !== null
      ? 'Справочник юридических лиц не приехал. Повторите через минуту; если не поможет, скажите администратору.'
      : 'Юридических лиц в справочнике нет: их заводит администратор.';

  if (заведена) {
    return (
      <div className='flex items-start gap-2 border-t border-stroke-soft-200 pt-3'>
        <RiBuilding2Line className='mt-0.5 size-4 shrink-0 text-text-soft-400' />
        <span className='text-paragraph-sm text-text-sub-600'>
          Ведомость аппарата управления за этот месяц уже заведена, она стоит первой в списке слева.
          В ней оклады, а не часы.
        </span>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-2 border-t border-stroke-soft-200 pt-3'>
      <div className='flex flex-col gap-0.5'>
        <span className='text-label-sm text-text-strong-950'>Аппарат управления</span>
        <span className='text-paragraph-xs text-text-sub-600'>
          Отдельная ведомость для офиса: объекта у неё нет, в ней оклады, а не часы. Выберите
          юрлицо, на которое её завести.
        </span>
      </div>
      <SelectField
        id='sheet-admin-entity'
        label='Юридическое лицо'
        value={юрлицо}
        pending={юрлица.isPending}
        failed={юрлица.isError}
        failedError={юрлица.error as ApiError | null}
        options={(юрлица.data?.items ?? []).map((пункт) => ({
          value: пункт.id,
          label: пункт.short_name ?? пункт.name,
        }))}
        onChange={setЮрлицо}
        placeholder='выберите юрлицо'
        emptyNote={проЮрлица}
      />
      <Button.Root
        variant='neutral'
        mode='stroke'
        size='small'
        onClick={() => {
          if (!можноПравить) {
            setЗапрет('Ведомости заводит финансовый отдел или администратор.');
            return;
          }
          if (юрлицо === NO_VALUE) {
            setЗапрет(
              'Без юрлица административную ведомость завести нельзя: объекта у неё нет, и взять юрлицо программе неоткуда.',
            );
            return;
          }
          setЗапрет(null);
          создание.mutate({
            year: период.year,
            month: период.month,
            site_id: null,
            legal_entity_id: юрлицо,
          });
        }}
      >
        {создание.isPending ? 'Заводим…' : 'Завести ведомость аппарата управления'}
      </Button.Root>
      {запрет !== null && <p className='text-paragraph-xs text-text-sub-600'>{запрет}</p>}
      {создание.error !== null && <ErrorAlert error={создание.error} />}
      {создание.data !== undefined && (
        <p className='text-paragraph-sm text-text-sub-600'>
          Заведена: {создание.data.legal_entity_name}, {создание.data.period_title}. Она стоит
          первой в списке слева.
        </p>
      )}
    </div>
  );
}
